import { NavItemBaseProps } from 'src/components/nav-section';
import { adminLeftMenu } from 'src/layouts/configs/menus';

// ----------------------------------------------------------------------
// Groups/sorts the permission list by the feature clusters of the left menu
// (`adminLeftMenu`), so assigning permissions mirrors the screens an operator
// actually sees. The menu stays the single source of truth: adding a screen to
// `menus.ts` automatically files its permissions under the right cluster.
// ----------------------------------------------------------------------

/** Section used for permissions whose resource is not referenced by any menu item. */
export const UNGROUPED_SECTION_KEY = 'resource.sectionUngrouped';

/** Actions listed first, in CRUD order; anything else falls back to alphabetical. */
const ACTION_PRIORITY = ['read', 'create', 'update', 'delete', 'write', 'export'];

export type MenuResourceInfo = {
  /** i18n key of the menu section (subheader), e.g. `resource.sectionUserCenter`. */
  sectionKey: string;
  /** Position of the section in the left menu. */
  sectionOrder: number;
  /** Position of the menu item inside its section (depth-first). */
  itemOrder: number;
  /** i18n key of the menu item, e.g. `resource.userInfo`. */
  titleKey: string;
  /** i18n keys of the parent menu items, outermost first. */
  parentTitleKeys: string[];
};

export type PermissionItem = {
  id: string;
  resource: string;
  action: string;
  groupName?: string | null;
  [key: string]: any;
};

export type PermissionResourceNode = {
  /** Stable tree node id, unique across sections. */
  nodeId: string;
  resource: string;
  /** Menu item this resource belongs to, when it is reachable from the left menu. */
  menu?: MenuResourceInfo;
  /** How `menu` was found: the screen's own resource, or a `prefix` sub-resource of it. */
  menuMatch?: MenuMatch;
  /** What a sub-resource adds on top of its screen, e.g. `period`. */
  resourceSuffix?: string;
  /** `groupName` coming from the API — kept for search and for ungrouped resources. */
  legacyGroupName: string | null;
  actions: PermissionItem[];
  actionIds: string[];
};

/**
 * A menu item that only groups other items (`Blind Box`, `Swap`) — it guards no
 * permission of its own, so it exists here purely to reproduce the shape of the
 * left menu, which nests up to three levels deep.
 */
export type PermissionGroupNode = {
  nodeId: string;
  /** i18n key of the grouping menu item. */
  titleKey: string;
  groups: PermissionGroupNode[];
  resources: PermissionResourceNode[];
  actionIds: string[];
};

export type PermissionSectionNode = {
  /** Stable tree node id. */
  nodeId: string;
  /** i18n key of the section label. */
  sectionKey: string;
  /** Nested menu groups, in menu order. */
  groups: PermissionGroupNode[];
  /** Resources sitting directly under the section, i.e. not inside any group. */
  resources: PermissionResourceNode[];
  actionIds: string[];
};

// ----------------------------------------------------------------------

const buildResourceIndex = (): Map<string, MenuResourceInfo> => {
  const index = new Map<string, MenuResourceInfo>();

  adminLeftMenu.forEach((section, sectionOrder) => {
    const sectionKey = section.subheader;
    if (!sectionKey) return;

    let itemOrder = 0;

    const walk = (items: NavItemBaseProps[], parentTitleKeys: string[]) => {
      items.forEach((item) => {
        if (!item) return;

        // Every screen has its own permission, so the first (and only) menu
        // item claiming a resource defines where it belongs.
        if (item.resource && !index.has(item.resource)) {
          index.set(item.resource, {
            sectionKey,
            sectionOrder,
            itemOrder: (itemOrder += 1),
            titleKey: item.title,
            parentTitleKeys,
          });
        }

        if (item.children?.length) {
          walk(item.children as NavItemBaseProps[], [...parentTitleKeys, item.title]);
        }
      });
    };

    walk(section.items || [], []);
  });

  return index;
};

/** resource -> where it lives in the left menu. First occurrence in the menu wins. */
export const MENU_RESOURCE_INDEX = buildResourceIndex();

/** Section i18n keys in left-menu order, with the catch-all section last. */
export const MENU_SECTION_KEYS: string[] = [
  ...adminLeftMenu.map((section) => section.subheader).filter((key): key is string => !!key),
  UNGROUPED_SECTION_KEY,
];

const SECTION_ORDER = new Map(MENU_SECTION_KEYS.map((key, order) => [key, order]));

/**
 * Many permissions are sub-resources of a screen that *is* in the menu
 * (`dashboard-v3-users` → `dashboard-v3`). Walk the resource name back to its
 * parent so those land next to the screen they belong to instead of in the
 * catch-all section.
 *
 * A shared namespace is deliberately not enough: `xpx-perps:user-risk-class` is
 * used by User Info, not by the first `xpx-perps:*` screen in the menu, and
 * filing it there shows a subnav group rows the sidebar does not have.
 */
export type MenuMatch = 'exact' | 'prefix';

type MenuResolution = { menu: MenuResourceInfo; match: MenuMatch; suffix?: string };

/**
 * Permissions of a screen that is a tab of another one: it has a permission of its own (so a role can grant it) but no menu
 * entry, so it files under the screen that holds the tab. `vault:approvals` is the "Waiting for signers" tab of Operations.
 */
const TAB_OF_SCREEN: Record<string, string> = { 'vault:approvals': 'vault:operations' };

const resolveByResourceName = (resource: string): MenuResolution | undefined => {
  const exact = MENU_RESOURCE_INDEX.get(resource);
  if (exact) return { menu: exact, match: 'exact' };

  const host = TAB_OF_SCREEN[resource] && MENU_RESOURCE_INDEX.get(TAB_OF_SCREEN[resource]);
  if (host) return { menu: host, match: 'prefix', suffix: 'waiting-for-signers' };

  const segments = resource.split('-');
  for (let size = segments.length - 1; size > 0; size -= 1) {
    const parent = MENU_RESOURCE_INDEX.get(segments.slice(0, size).join('-'));
    if (parent) return { menu: parent, match: 'prefix', suffix: segments.slice(size).join('-') };
  }

  return undefined;
};

const humanize = (value: string) =>
  value
    .split(/[-_:]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Row label for a resource: the subnav title of the screen it guards, or — for a
 * sub-resource (`world-cup-leaderboard-period`) — that title plus what it adds,
 * so it never reads the same as the screen itself.
 */
export const getResourceLabel = (node: PermissionResourceNode, translate: (key: string) => string) => {
  if (!node.menu) return '';
  const screen = translate(node.menu.titleKey);
  return node.resourceSuffix ? `${screen} › ${humanize(node.resourceSuffix)}` : screen;
};

const compareAction = (a: PermissionItem, b: PermissionItem) => {
  const aRank = ACTION_PRIORITY.indexOf(a.action);
  const bRank = ACTION_PRIORITY.indexOf(b.action);
  if (aRank !== bRank) return (aRank === -1 ? ACTION_PRIORITY.length : aRank) - (bRank === -1 ? ACTION_PRIORITY.length : bRank);
  return a.action.localeCompare(b.action);
};

const compareResource = (a: PermissionResourceNode, b: PermissionResourceNode) => {
  // Menu-backed resources keep the menu order; the rest fall back to their legacy
  // group name so leftovers still read as coherent blocks.
  if (a.menu && b.menu) {
    if (a.menu.itemOrder !== b.menu.itemOrder) return a.menu.itemOrder - b.menu.itemOrder;
    // Sub-resources share their parent's order — keep them next to it, alphabetically.
    return a.resource.localeCompare(b.resource);
  }
  if (a.menu) return -1;
  if (b.menu) return 1;

  const aGroup = a.legacyGroupName || '';
  const bGroup = b.legacyGroupName || '';
  if (aGroup !== bGroup) return aGroup.localeCompare(bGroup);
  return a.resource.localeCompare(b.resource);
};

/**
 * Flattens the `/permissions` payload (`[{groupName, resources: [{resource, actions}]}]`)
 * into a plain permission list.
 */
export const flattenPermissionGroups = (groups: any[]): PermissionItem[] =>
  (groups || []).flatMap((group: any) =>
    (group?.resources || []).flatMap((resource: any) =>
      (resource?.actions || []).map((action: any) => ({
        ...action,
        resource: action.resource ?? resource.resource,
        groupName: action.groupName ?? group.groupName ?? null,
      }))
    )
  );

/** Groups a flat permission list into left-menu sections, sorted like the menu. */
export const groupPermissionsByMenuSection = (permissions: PermissionItem[]): PermissionSectionNode[] => {
  const sections = new Map<string, Map<string, PermissionResourceNode>>();

  (permissions || []).forEach((permission) => {
    if (!permission?.resource) return;

    // The menu is the only thing that decides a section. A resource it does not
    // know about goes to the catch-all rather than borrowing a section from its
    // API `groupName` — that guesswork put permissions under screens that never
    // list them, and it is the menu, not this file, that owns those gaps.
    const resolution = resolveByResourceName(permission.resource);
    const menu = resolution?.menu;
    const sectionKey = menu?.sectionKey ?? UNGROUPED_SECTION_KEY;

    if (!sections.has(sectionKey)) sections.set(sectionKey, new Map());
    const resources = sections.get(sectionKey)!;

    if (!resources.has(permission.resource)) {
      resources.set(permission.resource, {
        nodeId: `${sectionKey}::${permission.resource}`,
        resource: permission.resource,
        menu,
        menuMatch: resolution?.match,
        resourceSuffix: resolution?.suffix,
        legacyGroupName: permission.groupName || null,
        actions: [],
        actionIds: [],
      });
    }

    const node = resources.get(permission.resource)!;
    node.actions.push(permission);
    node.actionIds.push(permission.id);
  });

  return Array.from(sections.entries())
    .map(([sectionKey, resources]) => {
      const resourceNodes = Array.from(resources.values())
        .map((node) => ({ ...node, actions: [...node.actions].sort(compareAction) }))
        .sort(compareResource);

      return nestUnderMenuGroups(sectionKey, resourceNodes);
    })
    .sort(
      (a, b) =>
        (SECTION_ORDER.get(a.sectionKey) ?? MENU_SECTION_KEYS.length) -
        (SECTION_ORDER.get(b.sectionKey) ?? MENU_SECTION_KEYS.length)
    );
};

/**
 * Rebuilds the grouping levels of the left menu around a section's resources.
 * `Blind Box` and `Swap` are menu items that only contain other items, and a
 * flat list of ~40 resources under one section loses the structure an operator
 * navigates by, so each resource is filed under the same chain of parents it
 * sits under in the menu (`parentTitleKeys`, outermost first).
 */
const nestUnderMenuGroups = (
  sectionKey: string,
  resourceNodes: PermissionResourceNode[]
): PermissionSectionNode => {
  const section: PermissionSectionNode = {
    nodeId: `section::${sectionKey}`,
    sectionKey,
    groups: [],
    resources: [],
    actionIds: [],
  };

  resourceNodes.forEach((node) => {
    const trail = node.menu?.parentTitleKeys || [];
    let groups: PermissionGroupNode[] = section.groups;
    let target: PermissionGroupNode | undefined;

    trail.forEach((titleKey, depth) => {
      let group = groups.find((g) => g.titleKey === titleKey);
      if (!group) {
        group = {
          // The trail is part of the id so two groups sharing a label under
          // different parents stay distinct nodes.
          nodeId: `group::${sectionKey}::${trail.slice(0, depth + 1).join('/')}`,
          titleKey,
          groups: [],
          resources: [],
          actionIds: [],
        };
        groups.push(group);
      }
      target = group;
      groups = group.groups;
    });

    (target ? target.resources : section.resources).push(node);
  });

  // Roll action ids up so a group checkbox can toggle everything beneath it.
  const collect = (group: PermissionGroupNode): string[] => {
    group.actionIds = [
      ...group.resources.flatMap((r) => r.actionIds),
      ...group.groups.flatMap(collect),
    ];
    return group.actionIds;
  };
  section.actionIds = [
    ...section.resources.flatMap((r) => r.actionIds),
    ...section.groups.flatMap(collect),
  ];

  return section;
};

// ----------------------------------------------------------------------

const normalize = (value: string) => value.toLowerCase().trim();

/**
 * Filters the grouped tree against a free-text query. Matching a section or a
 * resource keeps its whole subtree; matching only an action keeps that action.
 *
 * @param resolveLabel translates an i18n key, so the search also covers the
 *                     labels the operator actually reads on screen.
 */
export const filterPermissionSections = (
  sections: PermissionSectionNode[],
  query: string,
  resolveLabel: (key: string) => string
): PermissionSectionNode[] => {
  const keyword = normalize(query);
  if (!keyword) return sections;

  const matches = (value?: string | null) => !!value && normalize(value).includes(keyword);

  const filterResources = (nodes: PermissionResourceNode[], inherited: boolean) =>
    nodes
      .map((node) => {
        const resourceMatched =
          inherited ||
          matches(node.resource) ||
          matches(node.legacyGroupName) ||
          (!!node.menu && matches(getResourceLabel(node, resolveLabel)));

        const actions = resourceMatched
          ? node.actions
          : node.actions.filter(
              (action) => matches(action.action) || matches(`${node.resource}:${action.action}`)
            );

        if (!actions.length) return null;
        return { ...node, actions, actionIds: actions.map((action) => action.id) };
      })
      .filter((node): node is PermissionResourceNode => !!node);

  // Matching a group keeps everything under it, the same way matching a section does.
  const filterGroup = (group: PermissionGroupNode, inherited: boolean): PermissionGroupNode | null => {
    const groupMatched = inherited || matches(resolveLabel(group.titleKey));
    const resources = filterResources(group.resources, groupMatched);
    const groups = group.groups
      .map((child) => filterGroup(child, groupMatched))
      .filter((child): child is PermissionGroupNode => !!child);

    if (!resources.length && !groups.length) return null;
    return {
      ...group,
      resources,
      groups,
      actionIds: [...resources.flatMap((r) => r.actionIds), ...groups.flatMap((g) => g.actionIds)],
    };
  };

  return sections
    .map((section) => {
      const sectionMatched = matches(section.sectionKey) || matches(resolveLabel(section.sectionKey));
      const resources = filterResources(section.resources, sectionMatched);
      const groups = section.groups
        .map((group) => filterGroup(group, sectionMatched))
        .filter((group): group is PermissionGroupNode => !!group);

      if (!resources.length && !groups.length) return null;
      return {
        ...section,
        resources,
        groups,
        actionIds: [...resources.flatMap((r) => r.actionIds), ...groups.flatMap((g) => g.actionIds)],
      };
    })
    .filter((section): section is PermissionSectionNode => !!section);
};

/** Every tree node id in the given sections — used to expand/collapse everything. */
export const collectExpandableIds = (sections: PermissionSectionNode[]): string[] => {
  const fromGroup = (group: PermissionGroupNode): string[] => [
    group.nodeId,
    ...group.resources.map((node) => node.nodeId),
    ...group.groups.flatMap(fromGroup),
  ];
  return sections.flatMap((section) => [
    section.nodeId,
    ...section.resources.map((node) => node.nodeId),
    ...section.groups.flatMap(fromGroup),
  ]);
};
